import React, { useState, useEffect, useRef } from "react";
import { Table, Switch, Button, Modal, Form, Input, InputNumber, Select, Slider, Tag, Typography, message, Popconfirm, Alert, Spin } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Header } from "../../../../components";
import { CONNECT_API } from "../../../../services/index";
import {
  useFetchLineChannels,
  useCreateLineChannel,
  useUpdateLineChannel,
  useDeleteLineChannel,
  useFetchLineRemarks,
  useCreateLineRemark,
  useUpdateLineRemark,
  useDeleteLineRemark,
} from "../hooks/useFetch";

// ต้องตรงกับ key ใน BackEnd/src/api/v1/lineCase/lineCaseFlows.js
const FLOW_OPTIONS = [
  { value: "deposit", label: "แจ้งฝาก" },
  { value: "withdraw", label: "แจ้งถอน (ยังไม่เปิด)" },
];

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

function LineChannelPage() {
  const companyId = sessionStorage.getItem("activeCompanyId");
  const { data: channels = [], isLoading, refetch } = useFetchLineChannels(companyId);
  const { mutateAsync: createChannel, isLoading: isCreating } = useCreateLineChannel();
  const { mutateAsync: updateChannel } = useUpdateLineChannel();
  const { mutateAsync: deleteChannel } = useDeleteLineChannel();

  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      liffId: record.liff_id,
      channelId: record.channel_id,
      // ⚠️ ไม่ดึงค่าลับเดิมมาโชว์ (backend ก็ไม่ส่งมาให้) — เว้นว่าง = คงของเดิม
      channelSecret: "",
      accessToken: "",
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await updateChannel({ id: editing.id, ...values });
        message.success("แก้ไขสำเร็จ");
      } else {
        await createChannel({ ...values, enabled: false });
        message.success("เพิ่มสำเร็จ — เปิดใช้งานได้จากสวิตช์ในตาราง");
      }
      setModalOpen(false);
      refetch();
    } catch (error) {
      if (error?.errorFields) return; // ฟอร์มไม่ผ่าน antd แสดงใต้ช่องแล้ว
      message.error(errorMessage(error, "บันทึกไม่สำเร็จ"));
    }
  };

  const handleToggle = async (record, checked) => {
    setTogglingId(record.id);
    try {
      await updateChannel({ id: record.id, enabled: checked });
      refetch();
    } catch (error) {
      message.error(errorMessage(error, "เปลี่ยนสถานะไม่สำเร็จ"));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (record) => {
    try {
      await deleteChannel(record.id);
      message.success("ลบสำเร็จ");
      refetch();
    } catch (error) {
      message.error(errorMessage(error, "ลบไม่สำเร็จ"));
    }
  };

  const secretTag = (isSet, label) =>
    isSet ? <Tag color="green">ตั้งค่าแล้ว</Tag> : <Tag color="default">ยังไม่ตั้ง ({label})</Tag>;

  const columns = [
    {
      title: "ใช้งาน",
      dataIndex: "enabled",
      width: 90,
      render: (enabled, record) => (
        <Switch
          checked={!!enabled}
          loading={togglingId === record.id}
          onChange={(checked) => handleToggle(record, checked)}
        />
      ),
    },
    { title: "ชื่อ", dataIndex: "name" },
    { title: "LIFF ID", dataIndex: "liff_id", render: (v) => <code>{v}</code> },
    { title: "Channel ID", dataIndex: "channel_id", render: (v) => <code>{v}</code> },
    {
      title: "Channel Secret",
      dataIndex: "channelSecretSet",
      width: 150,
      render: (v) => secretTag(v, "ต้องใส่ (เมนูเช็คสถานะ)"),
    },
    {
      title: "Access Token",
      dataIndex: "accessTokenSet",
      width: 150,
      render: (v) => secretTag(v, "ต้องใส่ (เมนูเช็คสถานะ)"),
    },
    {
      // เอาไปวางที่ LINE Developers Console → Messaging API channel → Webhook URL
      title: "Webhook URL",
      render: (_, record) => (
        <Typography.Text copyable code style={{ fontSize: 12 }}>
          {`${CONNECT_API}/api/v1/lineCase/webhook/${record.id}`}
        </Typography.Text>
      ),
    },
    {
      title: "จัดการ",
      width: 120,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title="ลบ channel นี้?"
            description="หน้า LIFF ที่ใช้ channel นี้จะใช้งานไม่ได้ทันที"
            onConfirm={() => handleDelete(record)}
            okText="ลบ"
            cancelText="ยกเลิก"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="m-4 md:m-10 mt-24 p-4 md:p-10 bg-white rounded-3xl">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Header category="Admin" title="LINE OA" />
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
          เพิ่ม channel
        </Button>
      </div>

      <p style={{ color: "#888", marginBottom: 12 }}>
        ตั้งค่า LINE OA ที่ใช้รับเคสจากลูกค้าผ่านหน้า LIFF · ค่าทั้งหมดเอามาจาก LINE Developers Console
      </p>

      {/* access token ส่งข้อความในนาม OA ได้ — เตือนไว้ให้คนที่กรอกรู้ว่ากำลังถืออะไรอยู่ */}
      <Alert
        type="warning"
        showIcon
        className="mb-4"
        message="เมนู “เช็คสถานะ” ต้องใส่ Channel Secret + Access Token ของ Messaging API channel"
        description="หน้ารับเคสใช้แค่ LIFF ID กับ Channel ID (ของ LINE Login channel) · ส่วนการตอบการ์ดสถานะในแชทต้องใช้ Channel Secret (ตรวจว่าข้อความมาจาก LINE จริง) และ Channel Access Token (ใช้ตอบกลับ) ของ Messaging API channel แล้วนำ Webhook URL ในตารางไปตั้งใน LINE Developers Console — Access Token ส่งข้อความในนาม OA ได้ ห้ามส่งต่อให้ใคร"
      />

      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={channels}
        columns={columns}
        pagination={false}
        scroll={{ x: "max-content" }}
      />

      <ThemeSection channels={channels} onSaved={refetch} />

      <RemarkSection companyId={companyId} />

      <Modal
        title={editing ? "แก้ไข LINE channel" : "เพิ่ม LINE channel"}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText="บันทึก"
        cancelText="ยกเลิก"
        confirmLoading={isCreating}
        width={620}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="ชื่อเรียก" rules={[{ required: true, message: "กรอกชื่อ" }]}>
            <Input placeholder="เช่น LINE OA หลัก" />
          </Form.Item>
          <Form.Item
            name="liffId"
            label="LIFF ID"
            rules={[
              { required: true, message: "กรอก LIFF ID" },
              // LIFF ID = <channelId>-<8 ตัวอักษร> เสมอ · ถ้าไม่มีขีดแปลว่ากรอก Channel ID มาแทน
              // ซึ่งเป็นความผิดพลาดที่เกิดจริงมาแล้ว และไปพังตอนลูกค้าเปิดหน้า (liff.init ไม่ผ่าน)
              {
                pattern: /^\d+-[A-Za-z0-9]+$/,
                message: "รูปแบบไม่ถูก — LIFF ID ต้องมีขีดกลาง เช่น 2011794447-Xy3kPq7B (ถ้าไม่มีขีด แปลว่ากำลังกรอก Channel ID อยู่)",
              },
            ]}
            extra="สร้าง LIFF app ไว้ใต้ LINE Login channel เท่านั้น (ตั้งแต่ปี 2019 เพิ่มใน Messaging API channel ไม่ได้แล้ว) — Scope ต้องติ๊ก openid ไม่งั้นจะไม่มี ID token ให้ยืนยันตัวตน"
          >
            <Input placeholder="2000000001-abcdefg" />
          </Form.Item>
          <Form.Item
            name="channelId"
            label="Channel ID"
            dependencies={["liffId"]}
            rules={[
              { required: true, message: "กรอก Channel ID" },
              // เตือน (ไม่บล็อก) เมื่อไม่ตรงกับเลขหน้าขีดของ LIFF ID
              // LIFF app อยู่ใต้ LINE Login channel ตัวไหน ID token ก็ออกในนาม channel ตัวนั้น
              // ถ้ากรอกของ Messaging API channel มาแทน จะผ่านหน้า LIFF แต่ไปพังตอนยืนยันตัวตน
              ({ getFieldValue }) => ({
                warningOnly: true,
                validator(_, value) {
                  const liffId = getFieldValue("liffId") || "";
                  const prefix = String(liffId).split("-")[0];
                  if (!value || !prefix || String(value).trim() === prefix) return Promise.resolve();
                  return Promise.reject(
                    new Error(
                      `ปกติต้องเป็น ${prefix} (เลขหน้าขีดของ LIFF ID) — ถ้ากรอกของ Messaging API channel จะยืนยันตัวตนไม่ผ่าน`
                    )
                  );
                },
              }),
            ]}
            extra="⚠️ ของ LINE Login channel ไม่ใช่ของ Messaging API channel — ใช้ตรวจว่า ID token ที่ลูกค้าส่งมาเป็นของจริง และ LINE Login channel ต้องอยู่ provider เดียวกับ Messaging API channel ไม่งั้น LINE userId ที่เก็บได้จะเป็นคนละตัวกับที่ใช้ส่งข้อความ"
          >
            <Input placeholder="2000000001" />
          </Form.Item>
          <Form.Item
            name="channelSecret"
            label={editing ? "Channel Secret ของ Messaging API channel (เว้นว่าง = ใช้ของเดิม)" : "Channel Secret ของ Messaging API channel"}
            extra="LINE Developers Console → Messaging API channel → Basic settings → Channel secret (ไม่ใช่ของ LINE Login channel)"
          >
            <Input.Password placeholder="ต้องใส่ถ้าจะใช้เมนูเช็คสถานะ" autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="accessToken"
            label={editing ? "Channel Access Token ของ Messaging API channel (เว้นว่าง = ใช้ของเดิม)" : "Channel Access Token ของ Messaging API channel"}
            extra="Messaging API channel → แท็บ Messaging API → Channel access token (long-lived) กด Issue"
          >
            <Input.TextArea rows={3} placeholder="ต้องใส่ถ้าจะใช้เมนูเช็คสถานะ" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ผสมสีเข้ากับดำ/ขาว — ใช้วาดตัวอย่างให้ตรงกับที่หน้า LIFF คำนวณจริง
const mixHex = (hex, target, ratio) => {
  const n = parseInt(String(hex || "#06c755").slice(1), 16);
  const mix = (c) => Math.round(c + (target - c) * ratio);
  const r = mix((n >> 16) & 255);
  const g = mix((n >> 8) & 255);
  const b = mix(n & 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const BUTTON_STYLES = [
  { value: "flat", label: "สีเดียว" },
  { value: "gradient", label: "ไล่ 2 สี (แนวทแยง)" },
  { value: "metallic", label: "ไล่แบบโลหะ (เข้ม-สว่าง-เข้ม)" },
];

// ต้องให้ผลเหมือน buildSkin ใน public/liff/index.html เป๊ะ ไม่งั้นตัวอย่างหลอกตา
const buildSkin = (t) => {
  const c1 = t.brandColor;
  const c2 = t.brandColor2 || mixHex(c1, 0, 0.18);
  if (t.buttonStyle === "metallic") {
    const edge = mixHex(c1, 0, 0.3);
    const shine = mixHex(c2, 255, 0.45);
    const grad = `linear-gradient(180deg,${edge} 0%,${c1} 18%,${c2} 42%,${shine} 50%,${c2} 58%,${c1} 82%,${edge} 100%)`;
    // หัวการ์ดใช้ช่วงสีเข้ม ไม่เอาแถบวาว (ตัวหนังสือขาวบนแถบสว่างอ่านไม่ออก) — ต้องตรงกับฝั่ง LIFF
    return {
      head: `linear-gradient(135deg,${edge} 0%,${c1} 100%)`,
      btn: grad,
      border: `2px solid ${mixHex(c1, 0, 0.45)}`,
      shadow: "0 1px 2px rgba(0,0,0,.45)",
    };
  }
  if (t.buttonStyle === "gradient") {
    const diag = `linear-gradient(135deg,${c1} 0%,${c2} 100%)`;
    return { head: diag, btn: diag, border: "0", shadow: "none" };
  }
  return {
    head: `linear-gradient(135deg,${c1} 0%,${mixHex(c1, 0, 0.18)} 100%)`,
    btn: c1, border: "0", shadow: "none",
  };
};

const ColorField = ({ label, value, onChange }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
    <input
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: 44, height: 34, border: "1px solid #d9d9d9", borderRadius: 6, padding: 2, background: "#fff" }}
    />
    <div>
      <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
      <code style={{ fontSize: 12, color: "#888" }}>{value}</code>
    </div>
  </div>
);

/**
 * หน้าตาของหน้า LIFF — แก้สี/รูปพื้นหลังได้โดยไม่ต้องแก้โค้ดและไม่ต้อง deploy
 * มีตัวอย่างข้าง ๆ เพราะค่า "ความเข้มฝ้า" ดูจากตัวเลขอย่างเดียวเดาไม่ออกว่าอ่านออกหรือเปล่า
 */
function ThemeSection({ channels, onSaved }) {
  const { mutateAsync: updateChannel } = useUpdateLineChannel();
  const target = channels.find((c) => c.enabled) || channels[0];
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (target?.theme) setDraft({ ...target.theme });
  }, [target?.id, target?.theme]);

  if (!target) return null;
  if (!draft) return <Spin />;

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  // ใช้ <input type=file> ที่อยู่ใน DOM จริง (ผ่าน ref) — เดิมสร้าง input ลอย ๆ ไม่ได้แปะเข้า DOM
  // บางเบราว์เซอร์ (Safari / Chrome บางรุ่น) เก็บขยะทิ้งไปก่อนเลือกไฟล์เสร็จ onchange เลยไม่ทำงาน = กดแล้วเงียบ
  const onPickFile = (e) => {
    const file = e.target.files && e.target.files[0];
    // ล้างค่าไว้ เลือกไฟล์เดิมซ้ำจะได้ยิง onChange อีกรอบ
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return message.error("ไฟล์นี้ไม่ใช่รูปภาพ");
    // เตือนก่อนอัป — ฝั่ง backend ย่อให้อยู่แล้ว แต่ไฟล์ใหญ่มากจะอัปช้าและกิน RAM ตอนย่อ
    if (file.size > 8 * 1024 * 1024) return message.error("ไฟล์ใหญ่เกิน 8MB");
    const reader = new FileReader();
    reader.onerror = () => message.error("อ่านไฟล์รูปไม่ได้");
    reader.onload = () => {
      const dataUrl = reader.result;
      // ลองเปิดรูปก่อน — HEIC จาก iPhone เบราว์เซอร์ส่วนใหญ่เปิดไม่ได้ ถ้าปล่อยผ่านตัวอย่างจะว่างเฉย ๆ
      const img = new Image();
      img.onload = () => {
        set({ bgImageUrl: dataUrl, __upload: dataUrl });
        message.info(`เลือก ${file.name} แล้ว — กด "บันทึกหน้าตา" เพื่ออัปขึ้นระบบ`);
      };
      img.onerror = () => message.error("เบราว์เซอร์เปิดรูปนี้ไม่ได้ (เช่น HEIC) — แปลงเป็น JPG/PNG ก่อน");
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { __upload, bgImageUrl, ...rest } = draft;
      await updateChannel({
        id: target.id,
        // ถ้าเพิ่งเลือกรูปใหม่ ส่งเป็น base64 ให้ backend อัปขึ้น Spaces
        // ถ้าไม่ได้แตะรูป ไม่ต้องส่ง bgImageUrl ไปเลย (ส่ง null = สั่งเอารูปออก)
        bgImage: __upload || undefined,
        theme: { ...rest, ...(bgImageUrl === null ? { bgImageUrl: null } : {}) },
      });
      message.success("บันทึกหน้าตาแล้ว");
      onSaved();
    } catch (error) {
      message.error(errorMessage(error, "บันทึกไม่สำเร็จ"));
    } finally {
      setSaving(false);
    }
  };

  const overlay = (draft.overlay || 0) / 100;
  const preview = {
    minHeight: 190,
    borderRadius: 12,
    padding: 18,
    background: draft.bgImageUrl
      ? `linear-gradient(rgba(0,0,0,${overlay}),rgba(0,0,0,${overlay})), url("${draft.bgImageUrl}") center/cover`
      : draft.bgColor,
  };

  return (
    <div style={{ marginTop: 32 }}>
      <Header category="Admin" title="หน้าตาหน้า LIFF" />
      <p style={{ color: "#888", marginBottom: 12 }}>
        ใช้กับ channel “{target.name}” · แก้แล้วมีผลทันทีที่ลูกค้าเปิดหน้าใหม่ ไม่ต้อง deploy
      </p>

      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 260px", minWidth: 260 }}>
          <ColorField label="สีหลัก (หัวการ์ด ปุ่ม ตัวเลือก)" value={draft.brandColor} onChange={(v) => set({ brandColor: v })} />

          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>สไตล์ปุ่มและหัวการ์ด</div>
          <Select
            value={draft.buttonStyle}
            onChange={(v) => set({ buttonStyle: v })}
            options={BUTTON_STYLES}
            style={{ width: "100%", marginBottom: 14 }}
          />

          {draft.buttonStyle !== "flat" && (
            <ColorField
              label="สีที่สองของการไล่สี"
              value={draft.brandColor2 || mixHex(draft.brandColor, 0, 0.18)}
              onChange={(v) => set({ brandColor2: v })}
            />
          )}

          <ColorField label="สีพื้นหลัง (ใช้เมื่อไม่ได้ตั้งรูป)" value={draft.bgColor} onChange={(v) => set({ bgColor: v })} />

          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>รูปพื้นหลัง</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14, position: "relative" }}>
            {/* ซ่อนด้วยการทำให้มองไม่เห็น ไม่ใช่ display:none — Safari บางรุ่นไม่ยอมเปิดหน้าเลือกไฟล์ให้ input ที่ display:none */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onPickFile}
              style={{ position: "absolute", width: 1, height: 1, opacity: 0, overflow: "hidden", pointerEvents: "none" }}
              tabIndex={-1}
            />
            <Button onClick={() => fileInputRef.current?.click()}>เลือกรูป…</Button>
            {draft.bgImageUrl && <Button danger onClick={() => set({ bgImageUrl: null, __upload: undefined })}>เอารูปออก</Button>}
          </div>
          {draft.__upload && (
            <div style={{ fontSize: 12, color: "#d46b08", marginTop: -8, marginBottom: 14 }}>
              รูปใหม่ยังไม่ถูกบันทึก — กด "บันทึกหน้าตา" ด้านล่าง
            </div>
          )}

          <div style={{ fontWeight: 600, fontSize: 14 }}>ความเข้มฝ้าทับรูป</div>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 2 }}>
            รูปยิ่งสว่าง ยิ่งต้องเพิ่ม ไม่งั้นตัวหนังสือบนรูปอ่านไม่ออก
          </div>
          <Slider
            min={0}
            max={80}
            value={draft.overlay}
            onChange={(v) => set({ overlay: v })}
            disabled={!draft.bgImageUrl}
            tooltip={{ formatter: (v) => `${v}%` }}
          />

          <Button type="primary" loading={saving} onClick={save} style={{ marginTop: 8 }}>
            บันทึกหน้าตา
          </Button>
        </div>

        <div style={{ flex: "1 1 260px", minWidth: 260 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>ตัวอย่าง</div>
          <div style={preview}>
            <div style={{ background: "#fff", borderRadius: 10, overflow: "hidden", boxShadow: "0 8px 24px rgba(16,24,40,.12)" }}>
              <div style={{
                background: buildSkin(draft).head,
                color: "#fff", padding: "12px 14px", fontWeight: 700,
                textShadow: buildSkin(draft).shadow,
              }}>
                แจ้งฝาก
              </div>
              <div style={{ padding: 14 }}>
                <div style={{
                  border: `1.5px solid ${draft.brandColor}`, background: mixHex(draft.brandColor, 255, 0.88),
                  borderRadius: 10, padding: "9px 12px", fontSize: 13, fontWeight: 600, marginBottom: 10,
                }}>
                  ยอดไม่เข้า
                </div>
                <div style={{
                  background: buildSkin(draft).btn, color: "#fff", textAlign: "center",
                  border: buildSkin(draft).border, textShadow: buildSkin(draft).shadow,
                  borderRadius: 10, padding: "10px 0", fontWeight: 700, fontSize: 14,
                }}>
                  ส่งข้อมูล
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * ตัวเลือกหมายเหตุที่ลูกค้าเห็นบนหน้า LIFF
 * "อื่นๆ (ระบุเอง)" ไม่ต้องเพิ่มที่นี่ — หน้า LIFF เติมให้เองเสมอ
 */
function RemarkSection({ companyId }) {
  const { data: remarks = [], isLoading, refetch } = useFetchLineRemarks(companyId);
  const { mutateAsync: createRemark, isLoading: isCreating } = useCreateLineRemark();
  const { mutateAsync: updateRemark } = useUpdateLineRemark();
  const { mutateAsync: deleteRemark } = useDeleteLineRemark();

  const [newLabel, setNewLabel] = useState("");
  const [newFlow, setNewFlow] = useState("deposit");

  const save = async (fn, okText) => {
    try {
      await fn();
      if (okText) message.success(okText);
      refetch();
    } catch (error) {
      message.error(errorMessage(error, "บันทึกไม่สำเร็จ"));
    }
  };

  const handleAdd = () => {
    const label = newLabel.trim();
    if (!label) return message.error("กรอกข้อความก่อน");
    // ลำดับถัดจากตัวท้ายสุดของ flow นั้น ผู้ใช้ไม่ต้องคิดเลขเอง
    const sortOrder =
      Math.max(0, ...remarks.filter((r) => r.flow === newFlow).map((r) => r.sort_order || 0)) + 10;
    save(async () => {
      await createRemark({ flow: newFlow, label, sortOrder });
      setNewLabel("");
    }, "เพิ่มแล้ว");
  };

  const columns = [
    {
      title: "ลำดับ",
      dataIndex: "sort_order",
      width: 90,
      render: (v, record) => (
        <InputNumber
          size="small"
          min={0}
          value={v}
          style={{ width: 70 }}
          onChange={(value) =>
            value != null && value !== v && save(() => updateRemark({ id: record.id, sortOrder: value }))
          }
        />
      ),
    },
    {
      title: "งาน",
      dataIndex: "flow",
      width: 130,
      render: (v) => <Tag>{FLOW_OPTIONS.find((f) => f.value === v)?.label || v}</Tag>,
    },
    {
      title: "ข้อความที่ลูกค้าเห็น",
      dataIndex: "label",
      render: (v, record) => (
        <Typography.Paragraph
          style={{ margin: 0 }}
          editable={{
            onChange: (value) => {
              const label = (value || "").trim();
              if (label && label !== v) save(() => updateRemark({ id: record.id, label }), "แก้ไขแล้ว");
            },
          }}
        >
          {v}
        </Typography.Paragraph>
      ),
    },
    {
      title: "ใช้งาน",
      dataIndex: "enabled",
      width: 90,
      render: (v, record) => (
        <Switch
          checked={!!v}
          onChange={(checked) => save(() => updateRemark({ id: record.id, enabled: checked }))}
        />
      ),
    },
    {
      title: "",
      width: 60,
      render: (_, record) => (
        <Popconfirm
          title="ลบตัวเลือกนี้?"
          onConfirm={() => save(() => deleteRemark(record.id), "ลบแล้ว")}
          okText="ลบ"
          cancelText="ยกเลิก"
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ marginTop: 32 }}>
      <Header category="Admin" title="หมายเหตุบนหน้า LIFF" />
      <p style={{ color: "#888", marginBottom: 12 }}>
        ตัวเลือกที่ลูกค้าแตะเลือกตอนแจ้งเรื่อง · ข้อความที่เลือกจะไปอยู่ในบรรทัด “หมายเหตุ” ของโน้ตในเคส ·
        ระบบใส่ตัวเลือก “อื่นๆ (ระบุเอง)” ให้เองอยู่แล้ว ไม่ต้องเพิ่มเอง
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <Select value={newFlow} onChange={setNewFlow} options={FLOW_OPTIONS} style={{ width: 180 }} />
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onPressEnter={handleAdd}
          placeholder="เช่น ยอดไม่เข้า / เติมผิดบัญชี"
          maxLength={100}
          style={{ width: 320 }}
        />
        <Button type="primary" icon={<PlusOutlined />} loading={isCreating} onClick={handleAdd}>
          เพิ่ม
        </Button>
      </div>

      <Table
        rowKey="id"
        size="small"
        loading={isLoading}
        dataSource={remarks}
        columns={columns}
        pagination={false}
        locale={{ emptyText: "ยังไม่มีตัวเลือก — ลูกค้าจะเห็นเฉพาะ “อื่นๆ (ระบุเอง)”" }}
      />
    </div>
  );
}

export default LineChannelPage;
